import type { StoreApi } from "zustand/vanilla";
import { fitDepthSurface } from "./fit-depth-surface";
import type { DepthSnapshot } from "./depth-surface";
import type { CylinderFitter } from "./cylinder-fitting";
import { tracePipeSelection, type PipeRunSeed } from "./pipe-selection";
import { fitLodPipeSeed } from "./fit-lod-pipe-seed";
import type { PipeTraceSolver } from "./pipe-tracker";
import type { SurfaceRequest, SurfaceWandState } from "./surface-wand-context";

/**
 * @param cloud Active source and shared loader.
 * @param snapshot Click-local cylinder depth capture.
 * @param request Original Pipe run click and camera identity.
 * @param store Canvas-scoped selection owner.
 * @param options Owned worker adapters and request lifetime.
 * @returns Completion after the supported run or an explicit stop.
 */
export async function runPipeWandRequest(
  cloud: Parameters<typeof tracePipeSelection>[0],
  snapshot: DepthSnapshot,
  request: SurfaceRequest,
  store: StoreApi<SurfaceWandState>,
  options: {
    signal: AbortSignal;
    cancelled(): boolean;
    fitCylinder: CylinderFitter;
    solver: PipeTraceSolver;
  },
): Promise<void> {
  const updateInterval = 250;
  let seed: PipeRunSeed | undefined = await fitDepthSurface(
    snapshot,
    { ...request, shape: "cylinder", addTo: undefined },
    options.cancelled,
    options.fitCylinder,
  );
  if (options.cancelled()) return;
  if (!seed && options.solver.seed) {
    const cylinder = await fitLodPipeSeed(cloud, request.point, {
      signal: options.signal,
      cancelled: options.cancelled,
      fit: options.solver.seed.bind(options.solver),
    });
    if (options.cancelled()) return;
    if (cylinder) {
      seed = {
        cloudId: request.cloudId,
        cameraId: request.cameraId,
        origin: request.point.clone(),
        cylinder,
      };
    }
  }
  if (!seed) {
    store.setState({
      request: undefined,
      error:
        "No stable pipe seed found. Click a straight section with clearly resolved curvature.",
    });
    return;
  }
  store.setState({ request: undefined, error: undefined });
  let lastPublished = -Infinity;
  const selection = await tracePipeSelection(cloud, seed, {
    signal: options.signal,
    solver: options.solver,
    publish: (selection) => {
      if (options.cancelled()) return;
      const now = performance.now();
      if (
        selection.coverage === "growing" &&
        now - lastPublished < updateInterval
      ) {
        return;
      }
      lastPublished = now;
      store.setState({ selection });
    },
  });
  if (!options.cancelled()) {
    store.setState({
      selection,
      error: selection.mask.patches.length
        ? undefined
        : "No supported pipe continuation was found from this seed.",
    });
  }
}
